import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Plus, Loader2, Pencil, Trash2, X, Truck, Mail, Phone, Euro } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", contact_name: "", contact_email: "", contact_phone: "", vehicle_type: "", rate: "", notes: "" };

export default function CarriersPage() {
    const [carriers, setCarriers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/carriers");
            setCarriers(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(EMPTY); setModal({ mode: "create" }); };
    const openEdit = (c) => { setForm({ ...c, rate: c.rate ?? "" }); setModal({ mode: "edit", id: c.id }); };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, rate: Number(form.rate) || 0 };
            if (modal.mode === "create") await api.post("/carriers", payload);
            else await api.put(`/carriers/${modal.id}`, payload);
            toast.success("Transporteur enregistré");
            setModal(null);
            await load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        setSaving(false);
    };

    const remove = async (c) => {
        if (!confirm(`Supprimer "${c.name}" ?`)) return;
        await api.delete(`/carriers/${c.id}`);
        toast.success("Transporteur supprimé");
        await load();
    };

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-7xl mx-auto">
            <header className="flex flex-wrap items-end justify-between gap-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">Partenaires</div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Transporteurs</h1>
                    <p className="text-sm text-slate-400 mt-1">Fiche partenaire : contact, type de véhicule, tarif.</p>
                </div>
                <button onClick={openCreate} data-testid="tms-create-carrier-button" className="kz-btn-primary text-sm"><Plus size={16} /> Nouveau transporteur</button>
            </header>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading && <div className="text-slate-400 col-span-full"><Loader2 className="animate-spin inline mr-2" />Chargement…</div>}
                {!loading && carriers.map((c) => (
                    <article key={c.id} data-testid={`carrier-card-${c.name}`} className="rounded-2xl border border-slate-800 bg-[#0f172a] p-5 hover:border-orange-500/40 transition">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
                                    <Truck size={16} className="text-orange-400" />
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-white">{c.name}</div>
                                    <div className="text-xs text-slate-500">{c.vehicle_type}</div>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-white"><Pencil size={14} /></button>
                                <button onClick={() => remove(c)} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                            {c.contact_name && <li className="flex items-center gap-2"><span className="text-slate-500">👤</span> {c.contact_name}</li>}
                            {c.contact_email && <li className="flex items-center gap-2"><Mail size={12} className="text-slate-500" /> {c.contact_email}</li>}
                            {c.contact_phone && <li className="flex items-center gap-2"><Phone size={12} className="text-slate-500" /> {c.contact_phone}</li>}
                            {c.rate ? <li className="flex items-center gap-2"><Euro size={12} className="text-slate-500" /> Tarif indicatif : <span className="font-mono">{c.rate}</span></li> : null}
                        </ul>
                        {c.notes && <p className="mt-3 text-xs text-slate-400 italic">{c.notes}</p>}
                    </article>
                ))}
                {!loading && carriers.length === 0 && (
                    <div className="text-slate-500 col-span-full text-center py-10">Aucun transporteur enregistré.</div>
                )}
            </div>

            {modal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-5 overflow-y-auto">
                    <div className="bg-[#0f172a] rounded-2xl border border-slate-800 w-full max-w-2xl mt-8">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                            <h3 className="font-display font-semibold text-white">{modal.mode === "create" ? "Nouveau transporteur" : "Modifier transporteur"}</h3>
                            <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                        </div>
                        <form onSubmit={save} className="p-6 grid md:grid-cols-2 gap-4">
                            <F label="Nom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="carrier-form-name" />
                            <F label="Type de véhicule *" value={form.vehicle_type} onChange={(v) => setForm({ ...form, vehicle_type: v })} required testid="carrier-form-vehicle" />
                            <F label="Contact" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
                            <F label="Email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
                            <F label="Téléphone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
                            <F label="Tarif indicatif" type="number" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} />
                            <div className="md:col-span-2">
                                <label className="text-xs text-slate-400 mb-1.5 block">Notes</label>
                                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white" />
                            </div>
                            <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-slate-800 mt-2">
                                <button type="button" onClick={() => setModal(null)} className="kz-btn-ghost text-sm">Annuler</button>
                                <button type="submit" disabled={saving} data-testid="carrier-form-submit" className="kz-btn-primary text-sm">
                                    {saving && <Loader2 className="animate-spin" size={14} />} Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function F({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white"
            />
        </div>
    );
}
